import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, catchError, concat, concatMap, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';

import { ChecklistsApiService } from '@core/data/api/checklists-api.service';
import {
  checklistDueFromDto,
  checklistFromDto,
  checklistItemToDto,
  checklistOverdueLineFromDto,
  checklistScorecardFromDto,
} from '@core/data/api/checklists.mapper';
import { UpdateChecklistDto } from '@core/data/api/checklists.dto';
import { todayIso } from '@core/util/date.util';

import { ChecklistsApiActions, ChecklistsPageActions } from './checklists.actions';

/**
 * Bridges checklist actions to `/api/v1/business-model/checklists`.
 *
 * Every write answers the checklist as the server now holds it, so nothing here
 * refetches the catalogue after a save.
 */
@Injectable()
export class ChecklistsEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(ChecklistsApiService);

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.entered),
      switchMap(() =>
        this.api.listChecklists().pipe(
          map((rows) =>
            ChecklistsApiActions.loadSuccess({ checklists: rows.map(checklistFromDto) }),
          ),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * Creates one. Nothing sends a reference: the server mints `CL.1`, `CL.2` … from
   * the same counter as `PS.12`, and a client that guessed would reissue a number
   * somebody already quotes.
   */
  readonly create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.checklistCreated),
      // A double-clicked "New checklist" is one checklist, not two.
      exhaustMap(({ title, summary, items }) =>
        this.api
          .createChecklist({
            name: title,
            description: summary || null,
            // Nobody yet. That is a real state for a checklist somebody is still
            // writing — and it means nobody owes it, rather than everybody.
            assignedPositionGuids: [],
            assignedPersonaCodes: [],
            revisionSummary: 'Created',
            // Empty for the New Checklist button, which opens an empty one.
            // Whole for a checklist read out of a document, so it lands in one
            // call rather than being created and then filled.
            items: (items ?? []).map(checklistItemToDto),
          })
          .pipe(
            map((dto) =>
              ChecklistsApiActions.checklistUpdated({ checklist: checklistFromDto(dto) }),
            ),
            catchError((err: unknown) =>
              of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
            ),
          ),
      ),
    ),
  );

  /**
   * Metadata and assignments. A true patch — only what changed is sent.
   *
   * **An absent list leaves it alone; an empty list clears it.** A caller that
   * always sent `[]` would unassign every seat on a title edit, so the two cases
   * are kept apart here by only setting a key the change actually carries.
   */
  readonly save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.checklistSaved),
      // ONE AT A TIME, in the order they were made. With `mergeMap` several edits
      // to the same checklist were in flight together and the reducer took
      // whichever ANSWERED last — so a slower early save could overwrite a later
      // one, and the screen showed a value nobody had typed most recently.
      concatMap(({ checklistId, changes }) => {
        const body: UpdateChecklistDto = { revisionSummary: revisionSummaryFor(changes) };
        if (changes.title !== undefined) body.name = changes.title;
        if (changes.summary !== undefined) body.description = changes.summary;
        if (changes.assignedPositions !== undefined) {
          body.assignedPositionGuids = changes.assignedPositions.map((a) => a.id);
        }
        if (changes.assignedPersonas !== undefined) {
          body.assignedPersonaCodes = changes.assignedPersonas.map((a) => a.id);
        }
        return this.api.updateChecklist(checklistId, body).pipe(
          map((dto) => ChecklistsApiActions.checklistUpdated({ checklist: checklistFromDto(dto) })),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
          ),
        );
      }),
    ),
  );

  /** The whole list, in its order. Items are never patched one at a time. */
  readonly replaceItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.itemsReplaced),
      // Ordered, for the same reason the metadata save is: this sends the WHOLE
      // list, so two replacements racing means whichever answers last decides
      // what the checklist contains — and the loser's line is gone.
      concatMap(({ checklistId, items }) =>
        this.api
          .replaceItems(checklistId, {
            items: items.map(checklistItemToDto),
            revisionSummary: `${items.length} line${items.length === 1 ? '' : 's'}`,
          })
          .pipe(
            map((dto) =>
              ChecklistsApiActions.checklistUpdated({ checklist: checklistFromDto(dto) }),
            ),
            catchError((err: unknown) =>
              of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
            ),
          ),
      ),
    ),
  );

  /** Archives it. Ticks point at it, so the row stays and stops being offered. */
  readonly archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.checklistArchived),
      mergeMap(({ checklistId }) =>
        this.api.archiveChecklist(checklistId).pipe(
          map(() => ChecklistsApiActions.checklistRemoved({ checklistId })),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * One person's day — today or later; past days come from the snapshot reads.
   *
   * `mergeMap`, not `switchMap`: several screens ask for different people's days
   * at once and a later request must not cancel an earlier one.
   */
  readonly due$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.dueRequested),
      mergeMap(({ personaCode, on }) =>
        this.api.listDue(personaCode, on).pipe(
          map((rows) =>
            ChecklistsApiActions.dueLoaded({
              personaCode,
              // The server answers which day it took us to mean, which matters when
              // we omitted `on`: today where that person works is not necessarily
              // today in this browser.
              on: rows[0]?.dueDt ?? on ?? '',
              due: rows.map(checklistDueFromDto),
            }),
          ),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.dueFailed({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * A run of days in one call — My Checklists' today through the next 30 days.
   *
   * `mergeMap` for the same reason the single-day read uses it.
   */
  readonly dueRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.dueRangeRequested),
      mergeMap(({ personaCode, from, to }) =>
        this.api.listDueRange(personaCode, from, to).pipe(
          map((rows) =>
            ChecklistsApiActions.dueRangeLoaded({
              personaCode,
              from,
              to,
              due: rows.map(checklistDueFromDto),
            }),
          ),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.dueFailed({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * The lines someone missed and still owes — Your Tasks' Overdue lines and
   * MRP's weekly lines still open. From the snapshot rows, never the due read.
   */
  readonly overdue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.overdueRequested),
      mergeMap(({ personaCode }) => this.overdueFor$(personaCode).pipe(
        catchError((err: unknown) => of(ChecklistsApiActions.dueFailed({ error: toMessage(err) }))),
      )),
    ),
  );

  /**
   * One scorecard, counted by the backend. Filed under what was ASKED — `asOf`
   * null stays null, so today's answer is found under today's key whatever day
   * the server took today to be.
   *
   * `mergeMap`: the four boxes (today) and a past period are read side by side,
   * and stepping on must not cancel a read somebody is about to look at.
   */
  readonly scorecard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.scorecardRequested),
      mergeMap(({ personaCode, asOf }) =>
        this.api.readScorecard(personaCode, asOf ?? undefined).pipe(
          map((dto) =>
            ChecklistsApiActions.scorecardLoaded({ personaCode, asOf, scorecard: checklistScorecardFromDto(dto) }),
          ),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.scorecardFailed({ personaCode, asOf, error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * Takes a tick back, then says the counts moved and re-reads what the line
   * was listed from ({@link reread$}).
   *
   * The re-read is not belt and braces: `outstanding` is derived on the day, so
   * the badge is only right if the day is asked again.
   */
  readonly unCheckOff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.lineUnchecked),
      mergeMap(({ personaCode, on, itemId }) =>
        this.api.unCheckOff(itemId, { on, personaCode }).pipe(
          switchMap(() => concat(of(ChecklistsApiActions.checkoffsChanged({ personaCode })), this.reread$(personaCode, on))),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * Amends a tick already recorded, then re-reads what it was listed from. A
   * note or a photo moves no count, so the scorecards are left alone.
   */
  readonly amendCheckoff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.checkoffAmended),
      mergeMap(({ personaCode, on, itemId, notes, photos }) =>
        this.api.amendCheckoff(itemId, { on, personaCode, notes, photos }).pipe(
          switchMap(() => this.reread$(personaCode, on)),
          catchError((err: unknown) =>
            of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * After a checkoff write, re-reads what the line was listed from. The three
   * checkoff writes all end here.
   *
   * - **`on` today or later:** that day's due read.
   * - **`on` before today:** the line was an Overdue one, so the OVERDUE read —
   *   the due read is never asked for a past day (§2 "the past comes from the
   *   snapshots"). A ticked miss leaves it; an untick brings it back.
   *
   * "Today" is the browser's, as every screen keys its due reads.
   */
  private reread$(personaCode: string, on: string): Observable<Action> {
    if (on < todayIso()) return this.overdueFor$(personaCode);
    return this.api
      .listDue(personaCode, on)
      .pipe(
        map((rows) =>
          ChecklistsApiActions.dueLoaded({ personaCode, on, due: rows.map(checklistDueFromDto) }),
        ),
      );
  }

  /** One person's overdue lines, as an action — the overdue read and the re-read after ticking a missed line. */
  private overdueFor$(personaCode: string): Observable<Action> {
    return this.api
      .listOverdue(personaCode)
      .pipe(
        map((rows) =>
          ChecklistsApiActions.overdueLoaded({ personaCode, overdue: rows.map(checklistOverdueLineFromDto) }),
        ),
      );
  }

  /**
   * Ticks a line, then says the counts moved (the held scorecards are marked to
   * be asked again) and re-reads what the line was listed from ({@link reread$}).
   *
   * The tick answers the single checkoff just written, not the day — and
   * `outstanding` is a derived count on the day, so the honest way to update the
   * badge is to ask again rather than decrement it here and hope.
   */
  readonly checkOff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistsPageActions.lineCheckedOff),
      mergeMap(({ personaCode, on, itemId, notes, photos }) =>
        this.api
          .checkOff(itemId, { on, personaCode, notes, photos })
          .pipe(
            switchMap(() => concat(of(ChecklistsApiActions.checkoffsChanged({ personaCode })), this.reread$(personaCode, on))),
            catchError((err: unknown) =>
              of(ChecklistsApiActions.writeFailure({ error: toMessage(err) })),
            ),
          ),
      ),
    ),
  );
}

/**
 * What the revision history will say this save was.
 *
 * Named from the fields that actually changed, because "edited" on every row tells
 * somebody reading the history nothing they did not already know.
 */
function revisionSummaryFor(changes: Partial<{ title: unknown; summary: unknown; assignedPositions: unknown; assignedPersonas: unknown }>): string {
  const parts: string[] = [];
  if (changes.title !== undefined) parts.push('title');
  if (changes.summary !== undefined) parts.push('summary');
  if (changes.assignedPositions !== undefined) parts.push('seats');
  if (changes.assignedPersonas !== undefined) parts.push('people');
  return parts.length ? `Edited ${parts.join(', ')}` : 'Edited';
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
